import React, { useState } from 'react';
import { Button, Modal, Form, Input, message, Alert } from 'antd';
import { KeyOutlined } from '@ant-design/icons';
import api, { currentUser } from '../utils/api';

const firstError = (e) => {
  const d = e.response?.data || {};
  const v = d.old_password || d.new_password || d.detail || d.error;
  return Array.isArray(v) ? v.join(' ') : v || 'Could not change the password';
};

/** Optional `forced`: blocks the app until the temporary password is replaced. */
const ChangePassword = ({ forced = false }) => {
  const [open, setOpen] = useState(!!forced);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const save = async () => {
    const v = await form.validateFields();
    setSaving(true);
    try {
      await api.post('/auth/users/change-password/', { old_password: v.old_password, new_password: v.new_password });
      const u = currentUser() || {};
      localStorage.setItem('user', JSON.stringify({ ...u, must_change_password: false }));
      message.success('Password changed');
      setOpen(false);
      if (forced) window.location.reload();
    } catch (e) {
      message.error(firstError(e), 6);
    } finally {
      setSaving(false);
    }
  };

  const modal = (
    <Modal
      open={open}
      title={forced ? 'Set a new password' : 'Change my password'}
      okText="Change"
      confirmLoading={saving}
      onOk={save}
      onCancel={forced ? undefined : () => setOpen(false)}
      closable={!forced}
      maskClosable={!forced}
      keyboard={!forced}
      cancelButtonProps={forced ? { style: { display: 'none' } } : undefined}
      destroyOnClose
    >
      {forced && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="You signed in with a temporary password. Choose a new password before using the system."
        />
      )}
      <Form form={form} layout="vertical">
        <Form.Item name="old_password" label="Current password" rules={[{ required: true }]}>
          <Input.Password autoFocus />
        </Form.Item>
        <Form.Item
          name="new_password"
          label="New password"
          extra="At least 8 characters, not only numbers, not a common password"
          rules={[{ required: true }, { min: 8, message: 'At least 8 characters' }]}
        >
          <Input.Password />
        </Form.Item>
        <Form.Item
          name="confirm"
          label="Repeat new password"
          dependencies={['new_password']}
          rules={[{
            required: true,
          }, ({ getFieldValue }) => ({
            validator: (_, v) => (!v || v === getFieldValue('new_password')
              ? Promise.resolve()
              : Promise.reject(new Error('The two passwords differ'))),
          })]}
        >
          <Input.Password />
        </Form.Item>
      </Form>
    </Modal>
  );

  if (forced) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f3ef', padding: 24 }}>
        {modal}
      </div>
    );
  }

  return (
    <>
      <Button size="small" icon={<KeyOutlined />} onClick={() => { form.resetFields(); setOpen(true); }}>Password</Button>
      {modal}
    </>
  );
};

export default ChangePassword;
